import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { map, Observable, tap } from 'rxjs';
import { UtilServiceService } from "./index";
import {
  IDataAlegias,
  IDataCirugias,
  IDataEnfermedades,
  IDataEspecialidades,
  IDataMedicamento
} from "../interfaces/index";
import { IndexdbService } from '../../core/database/indexdb.service';

type OrderedFields = {
  alergias: Array<IDataAlegias>;
  cirugias: Array<IDataCirugias>;
  enfermedades: Array<IDataCirugias>;
  medicamentos: Array<IDataMedicamento>;
  previsiones: Array<any>;
};

@Injectable({
  providedIn: 'root',
})
export class ServiceFormAnamnesisService {
  private readonly endpointUrlBase = `${environment.baseURL}datos-medicos`;
  private readonly utils: UtilServiceService = inject(UtilServiceService);
  private readonly indexDbService: IndexdbService = inject(IndexdbService);

  constructor(
    private httpClient: HttpClient
  ) { }

  getDatosMedicos(): Observable<any> {
    return new Observable<any>(observer => {
      this.indexDbService.getItem('datos-medicos').subscribe(result => {
        if (result) {
          const data = result as any;
          const json = typeof data === 'object' && data.value ? JSON.parse(data.value) : null;
          observer.next(json);
          observer.complete();
        } else {
          this.httpClient.get<any>(this.endpointUrlBase).pipe(
            tap((response) => {
              if (response.data) {
                const key = 'nombre';
                const text = 'NO INFORMADO';
                const { alergias, cirugias, enfermedades, medicamentos, previsiones, ...rest } = response.data;
                const fields = { alergias, cirugias, enfermedades, medicamentos, previsiones };
                const orderedFields: Partial<OrderedFields> = {};
                for (const [fieldKey, value] of Object.entries(fields)) {
                  orderedFields[fieldKey as keyof typeof fields] = this.utils.reorderByPriority(value, key, text);
                }
                response.data = { ...orderedFields, ...rest };
              }
              this.indexDbService.setItem('datos-medicos', response).subscribe();
            })
          ).subscribe({
            next: (response) => {
              observer.next(response);
              observer.complete();
            },
            error: (err) => observer.error(err)
          });
        }
      });
    });
  }

  getEspecialidades(): Observable<IDataEspecialidades[]> {
    return this.indexDbService.getDatosMedicos().pipe(
      map(result => result?.especialidades ?? [])
    );
  }

  getAlergias(): Observable<IDataAlegias[]> {
    return this.indexDbService.getDatosMedicos().pipe(
      map(result => result?.alergias ?? [])
    );
  }

  getEnfermedades(): Observable<IDataEnfermedades[]> {
    return this.indexDbService.getDatosMedicos().pipe(
      map(result => result?.enfermedades ?? [])
    );
  }

  getCirugias(): Observable<IDataCirugias[]> {
    return this.indexDbService.getDatosMedicos().pipe(
      map(result => result?.cirugias ?? [])
    );
  }

  getMedicamentos(): Observable<IDataMedicamento[]> {
    return this.indexDbService.getDatosMedicos().pipe(
      map(result => result?.medicamentos ?? [])
    );
  }
}
